import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as actions from '../../../redux/actions/growthEngine';
import { calculateYoutubeBudget } from '../../../helper/calculation';
import { MetaAdPreview, createFormMediaRenderer } from './MetaAdPreview';
import { formatCampaignMoney, getTimezoneLabel } from '../../../helper/utility';
import { CampaignFeePaymentOptions } from './CampaignFeePaymentOptions';
import { PricingPlanModal } from '../../../components/PricingPlanModal';
import { useCampaignFeeQuote } from '../../../queries/growthEngine/campaignFee.query';

const META_PREVIEW_OBJECTIVES = [
    "More Streams",
    "Spotify Growth",
    "Playlist Growth",
    "Instagram Growth",
    "Promote Custom Link",
    "Promote an Event",
    "Sell Your Merch",
    "Promote Bio Link",
    "YouTube Growth",
];

export const StepSix = () => {
    const dispatch = useDispatch();
    const { formData } = useSelector(state => state.growthEngine);
    const { hasActiveSubscription } = useSelector((state) => state.billing);
    const [uploadedMedia, setUploadedMedia] = useState([]);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const isYoutubeAdsPath = formData.platformType === "YouTube" && formData.objective === "YouTube Growth";
    const isMetaPlatform = formData.platformType === "Facebook" || formData.platformType === "Instagram";
    const showCampaignFeeOptions = !hasActiveSubscription && isMetaPlatform && !isYoutubeAdsPath;
    const {
        data: campaignFee,
        isLoading: isCampaignFeeLoading,
        error: campaignFeeError,
    } = useCampaignFeeQuote({
        adAccountId: formData.adAccountId,
        budget: formData.budget,
        budgetType: formData.budgetType,
        enabled: showCampaignFeeOptions,
    });
    const selectedYoutube = formData.selectedYoutubeContent || {};

    useEffect(() => {
        const storedMedia = JSON.parse(localStorage.getItem("uploadedMedia")) || [];
        setUploadedMedia(storedMedia);
    }, []);

    useEffect(() => {
        if (!formData.adViewAs) {
            dispatch(actions.updateFormData({ adViewAs: 'facebook' }));
        }
    }, [formData.adViewAs, dispatch]);

    const dateFormatter = (isoString, type) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);

        if (type === 'half') {
            const dayNum = date.getDate();
            const monthName = date.toLocaleString("en-US", { month: "long" });
            const ordinalSuffix = (d) => {
                if (d > 3 && d < 21) return "th";
                switch (d % 10) {
                    case 1: return "st";
                    case 2: return "nd";
                    case 3: return "rd";
                    default: return "th";
                }
            };
            return `${dayNum}${ordinalSuffix(dayNum)} ${monthName}`;
        }

        return `${day}/${month}/${year}`;
    };

    const formatToAmPm = (timeString) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = String(minutes).padStart(2, '0');
        return `${formattedHours}:${formattedMinutes} ${period}`;
    };

    const selectedPage = formData.pageList?.find(page => page.id === formData.pageId);
    const adViewAs = formData.adViewAs || 'facebook';
    const facebookProfile = {
        name: selectedPage?.name || 'Facebook Page',
        image: selectedPage?.profile_picture,
    };
    const instagramProfile = {
        name: selectedPage?.instagram_name || selectedPage?.instagram_display_name || selectedPage?.name || 'Instagram',
        image: selectedPage?.instagram_profile_picture || selectedPage?.profile_picture,
    };
    const renderMedia = useMemo(
        () => createFormMediaRenderer(formData, uploadedMedia),
        [formData, uploadedMedia]
    );

    return (
        <div className="font-inter text-theme_clr1 mb-[61px] relative" data-tour="review-launch">
            <div className="heading text-white font-medium text-center mb-[28.5px]">
                <h5 className="text-[22.5px] mb-1">Summary</h5>
                <div className="description text-white text-opacity-80 text-[15px]">
                    Check your ad details and preview of what your ad will look like
                </div>
            </div>
            <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                    <div className="left-info lg:w-[50%]" data-tour="preview-ad">
                        <div className="heading mb-4">
                            <div className="title-name text-base font-medium mb-2">Preview your ad</div>
                            <div className="description font-medium text-theme_clr1 text-[15px] leading-normal">
                                {
                                    isYoutubeAdsPath ?
                                        "Your Video details" :
                                        `Toggle between platforms to see how your ad will look
                                        when it’s live.`
                                }
                            </div>
                        </div>

                        {META_PREVIEW_OBJECTIVES.includes(formData.objective) ? (
                            <>
                            <MetaAdPreview
                                adViewAs={adViewAs}
                                onViewChange={(view) => dispatch(actions.updateFormData({ adViewAs: view }))}
                                facebookProfile={facebookProfile}
                                instagramProfile={instagramProfile}
                                adCaption={formData.adCaption}
                                adHeadline={formData.adHeadline}
                                adDescription={formData.adDescription}
                                callToAction={formData.callToAction}
                                destinationUrl={formData.destinationUrl}
                                renderMedia={renderMedia}
                            />
                            {formData.objective === "YouTube Growth" && selectedYoutube?.title ? (
                                <div className="flex items-center gap-3 bg-slate-100 border border-black rounded-xl p-3 mt-3">
                                    <img
                                        src={selectedYoutube.thumbnail}
                                        alt={selectedYoutube.title}
                                        className={selectedYoutube.contentType === "channel"
                                            ? "w-12 h-12 rounded-full object-cover"
                                            : "w-[80px] h-[60px] rounded-md object-cover"}
                                    />
                                    <div className="min-w-0">
                                        <div className="text-xs uppercase tracking-wide text-slate-500">
                                            Destination {selectedYoutube.contentType === "video" ? "video" : "channel"}
                                        </div>
                                        <div className="text-sm font-semibold text-theme_clr1 truncate">{selectedYoutube.title}</div>
                                    </div>
                                </div>
                            ) : null}
                            </>
                        ) : null}
                        {
                            isYoutubeAdsPath ?
                                <>

                                    {formData.selectedYoutubeVideo ? (
                                        <>
                                            <div className="youtube-preview w-full bg-slate-100 border border-black rounded-xl overflow-hidden p-3 mt-3">
                                                <div className="w-full mb-2 relative group cursor-pointer">
                                                    <img
                                                        src={formData.selectedYoutubeVideo?.thumbnail}
                                                        alt="YouTube Preview"
                                                        className="w-full rounded-lg shadow-sm group-hover:opacity-90 transition"
                                                    />
                                                </div>
                                                <div className="text-base font-semibold text-theme_clr1 mb-3">
                                                    {formData.selectedYoutubeVideo?.title}
                                                </div>
                                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                    <span className="text-theme_clr1">YouTube Channel</span>
                                                    <span className="text-[#99989F] text-right">
                                                        {formData.selectedYoutubeVideo?.channelName}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                    <span className="text-theme_clr1">Release Date</span>
                                                    <span className="text-[#99989F] text-right">
                                                        {formData?.selectedYoutubeVideo?.releaseDate
                                                            ? dateFormatter(formData?.selectedYoutubeVideo?.releaseDate)
                                                            : ""}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap justify-between gap-2 mb-2">
                                                    <span className="text-theme_clr1">Video URL</span>
                                                    <a
                                                        href={formData.selectedYoutubeVideo?.videoUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[#99989F] text-right hover:text-theme_clr1 transition underline"
                                                    >
                                                        {formData.selectedYoutubeVideo?.videoUrl}
                                                    </a>
                                                </div>
                                            </div>

                                            <div className='wrapper text-sm bg-theme_clr2 bg-opacity-20 rounded-lg p-4 mt-3'>
                                                Please ensure your video does not have explicit or shocking content - if it does, it may be denied
                                                by YouTube's Ads Review. We reserve the right to charge a 5% processing fee for any refunded campaign.

                                            </div>
                                        </>
                                    ) : null}
                                </>
                                :
                                ''
                        }
                    </div>
                    <div className="right-info lg:w-[50%]" data-tour="check-details">
                        <div className="heading mb-4">
                            <div className="title-name text-base font-medium mb-2">Check the details</div>
                            <div className="description font-medium text-theme_clr1 text-[15px] leading-normal">
                                Confirm your selections before payment.
                            </div>
                        </div>
                        <div className="bg-[#F7FAFC] border border-black rounded-xl mb-4 p-4">
                            <div className="item text-base font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Goal</span>
                                <span className="text-theme_clr1 text-right">{formData.objective}</span>
                            </div>
                            {
                                formData.objective === "YouTube Growth" && !isYoutubeAdsPath && selectedYoutube?.title ? (
                                    <>
                                        <div className="item text-[14.5px] font-medium flex justify-between gap-3 mb-5">
                                            <span className="text-theme_clr1">YouTube {selectedYoutube.contentType === "video" ? "Video" : "Channel"}</span>
                                            <span className="text-[#99989F] text-right">{selectedYoutube.title}</span>
                                        </div>
                                        <div className="item text-[14.5px] font-medium flex justify-between gap-3 mb-5">
                                            <span className="text-theme_clr1">Destination</span>
                                            <a
                                                href={formData.destinationUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#99989F] text-right hover:text-theme_clr1 underline break-all"
                                            >
                                                {formData.destinationUrl}
                                            </a>
                                        </div>
                                    </>
                                ) : null
                            }
                            {
                                formData?.platformType === 'YouTube' ?
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Campaign Type</span>
                                        <span className="text-[#99989F] text-right">{formData.advertisingChannelSubType === 'VIDEO_TRUEVIEW_IN_STREAM' ? `📈 Cheapest Views` : `🗣 More Engagement`}</span>
                                    </div> : ''
                            }
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Start date</span>
                                <span className="text-[#99989F] text-right">{dateFormatter(formData.startDate)} at {formatToAmPm(formData.startTime)}</span>
                            </div>
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">End date</span>
                                <span className="text-[#99989F] text-right">
                                    {formData.endDate
                                        ? `${dateFormatter(formData.endDate)} at ${formatToAmPm(formData.endTime)}`
                                        : '-'}
                                </span>
                            </div>
                            {formData.timezone ? (
                                <div className="item text-[14.5px] font-medium flex justify-between gap-3 mb-5">
                                    <span className="text-theme_clr1">Timezone</span>
                                    <span className="text-[#99989F] text-right normal-case">{getTimezoneLabel(formData.timezone)}</span>
                                </div>
                            ) : null}
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Budget</span>
                                <span className="text-[#99989F] text-right">
                                    {formatCampaignMoney(formData.budget, formData.currency)}
                                    {formData.budgetType ? ` (${formData.budgetType})` : ""}
                                </span>
                            </div>
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Audience</span>
                                {
                                    isYoutubeAdsPath ?
                                        <span className="text-[#99989F] text-right">{formData.youtubeGender === 'All' ? 'All Gender' : formData.youtubeGender}, {formData.youtubeAgeRange.min}-{formData.youtubeAgeRange.max}</span>
                                        :
                                        <span className="text-[#99989F] text-right">{formData.targeting.gender === 'All' ? 'All Gender' : formData.targeting.gender}, {formData.targeting.ageRange.min}-{formData.targeting.ageRange.max}</span>
                                }
                            </div>
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Language</span>
                                {
                                    isYoutubeAdsPath ?
                                        <span className="text-[#99989F] text-right">
                                            {formData.youtubeTargetLanguage?.map(lng => lng.name).join(", ") || "All"}
                                        </span>
                                        :
                                        <span className="text-[#99989F] text-right">
                                            {formData.targeting.languages?.map(lng => lng.name).join(", ") || "All"}
                                        </span>
                                }
                            </div>
                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Locations</span>
                                {
                                    isYoutubeAdsPath ?
                                        <span className="text-[#99989F] text-right">
                                            {[
                                                (formData.youtubeTargetCountry || []).map(country => country.countryCode)
                                            ].join(", ") || "Global Markets"}
                                        </span>
                                        :
                                        <span className="text-[#99989F] text-right">
                                            {[
                                                ...(formData.targeting.location.countries || []).map(country => country.name),
                                                ...(formData.targeting.location.regions || []).map(region => region.name),
                                                ...(formData.targeting.location.cities || []).map(city => city.name)
                                            ].join(", ") || "Global"}
                                        </span>

                                }

                            </div>
                            {
                                (formData.objective === "More Streams" || formData.objective === "Spotify Growth" || formData.objective === "Playlist Growth" || formData.objective === "Instagram Growth"||
                                    formData.objective === "Promote Custom Link" ||formData.objective === "Promote an Event" || formData.objective === "Sell Your Merch" ||
                                    formData.objective === "Promote Bio Link" || (formData.objective === "YouTube Growth" && !isYoutubeAdsPath)
                                ) ?
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Interests</span>
                                        <span className="text-[#99989F] text-right">{formData.targeting.interests?.map(interest => interest.name).join(", ")}</span>
                                    </div> : null
                            }
                            {
                                isYoutubeAdsPath ?
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Channels</span>
                                        <span className="text-[#99989F] text-right">{
                                            formData.advertisingChannelSubType === 'VIDEO_TRUEVIEW_IN_STREAM' ?
                                                formData.youtubeTargetChannels?.map(chl => chl.title).join(", ") : 'N/A'}
                                        </span>
                                    </div> : null
                            }

                            <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                <span className="text-theme_clr1">Platform</span>
                                <span className="text-[#99989F] text-right">{
                                    ((formData.platformType === "Facebook" || formData.platformType === "Instagram") && formData.objective === 'Instagram Growth') ?
                                        'Instagram' : (formData.platformType === "Facebook" || formData.platformType === "Instagram") ?
                                            'Facebook,Instagram' : formData.platformType
                                }</span>
                                {/* Facebook, Instagram */}
                            </div>
                        </div>
                        {
                            formData.platformType === "YouTube" ?

                                <div className="payment-option mb-4">
                                    <div className="title-name text-[22.69px] font-medium mb-2">Payment Details</div>
                                    <div className="bg-[#F7FAFC] border border-black rounded-xl p-4">
                                        {(() => {
                                            const { totalBudget, processingFee, grandTotal, diffDays } =
                                                calculateYoutubeBudget({
                                                    budget: formData.budget,
                                                    budgetType: formData.budgetType,
                                                    startDate: formData.startDate,
                                                    endDate: formData.endDate,
                                                });

                                            return (
                                                <>
                                                    {/* Budget */}
                                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                                        <span className="text-theme_clr1">
                                                            {formData.budgetType === "daily"
                                                                ? `Total Budget(Daily × ${diffDays} Days)`
                                                                : "Total Budget"}
                                                        </span>
                                                        <span className="text-[#99989F] text-right">
                                                            ${totalBudget.toLocaleString()}
                                                        </span>
                                                    </div>

                                                    {/* Processing Fee */}
                                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                                        <span className="text-theme_clr1">Processing Fee (10%)</span>
                                                        <span className="text-[#99989F] text-right">
                                                            ${processingFee.toLocaleString()}
                                                        </span>
                                                    </div>

                                                    {/* Total */}
                                                    <div className="item text-[14.5px] font-semibold capitalize flex justify-between gap-3">
                                                        <span className="text-theme_clr1">Total Price</span>
                                                        <span className="text-[#99989F] text-right font-semibold">
                                                            ${grandTotal.toLocaleString()}
                                                        </span>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                                : ''
                        }
                        {showCampaignFeeOptions ? (
                            <CampaignFeePaymentOptions
                                quote={campaignFee?.quote}
                                isLoading={isCampaignFeeLoading}
                                errorMessage={
                                    campaignFeeError?.response?.data?.message ||
                                    (campaignFeeError ? "Unable to calculate the campaign fee." : "")
                                }
                                onUnlock={() => setShowPricingPlanModal(true)}
                            />
                        ) : null}
                    </div>
                </div>
                {
                    formData.platformType === "YouTube" ?
                        <div className='wrapper text-sm bg-theme_clr2 bg-opacity-20 flex items-center gap-3 rounded-lg p-4 mt-3'>
                            <label class="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" value="" class="sr-only peer" />
                                <div
                                    class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600
                            peer-focus:ring-4 peer-focus:ring-blue-300 transition-all duration-300">
                                </div>
                                <div
                                    class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full
                             peer-checked:translate-x-5 transition-transform duration-300">
                                </div>
                            </label>
                            <div>
                                Once submited, this campaign won't be able to be edited. If your campaign is denied by
                                Youtube's ads review, your ads budget will be refunded.
                            </div>
                        </div>
                        : ''
                }

            </div>
            <PricingPlanModal
                isOpen={showPricingPlanModal}
                onClose={() => setShowPricingPlanModal(false)}
                returnUrl={window.location.origin + "/admin/growth-engine/campaign"}
            />
        </div>
    )
}
